import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Activity, Inbox, KeyRound, PlugZap, RefreshCw, Webhook,
} from 'lucide-react'
import {
  ErpTable, KpiStrip, MetricCard, Panel, SectionHeader, StatusBadge, Td, Th,
} from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingRows } from '@/shared/ui/states'
import { useLoad } from '@/features/administration/hooks'
import { useCan } from '@/shared/auth/session'
import { fmtDateTime, fmtNumber, humanise } from '@/shared/lib/format'
import { cn } from '@/lib/utils'
import type { HealthCode, IntegrationResponse } from '@/shared/api/types'
import {
  listInboundEvents, listIntegrations, AUTH_KIND_LABEL,
} from './api'
import { ApiKeysTab } from './ApiKeysTab'
import { WebhooksTab } from './WebhooksTab'

const VIEWS = [
  { id: 'registry', label: 'Registry', icon: PlugZap },
  { id: 'inbound', label: 'Inbound events', icon: Inbox },
  { id: 'webhooks', label: 'Webhooks', icon: Webhook },
  { id: 'keys', label: 'API keys', icon: KeyRound },
] as const

const HEALTH_STYLE: Record<HealthCode, string> = {
  HEALTHY: 'text-emerald-700 dark:text-emerald-400',
  DEGRADED: 'text-amber-700 dark:text-amber-400',
  DOWN: 'text-red-700 dark:text-red-400',
  UNKNOWN: 'text-muted-foreground',
}

/**
 * Integrations.
 *
 * Every third party the system talks to, in one place: the outbound registry
 * with its health and quota, the inbound callback log, the outbound webhooks,
 * and the inbound API keys we issue.
 *
 * **There is no reveal control anywhere in this slice, and adding one is not a
 * design change — it is a change of purpose.** The backend has no endpoint that
 * returns a credential value, so a "show secret" button would have nothing to
 * call. What the operator gets is a fingerprint, a status, an expiry and a
 * last-used timestamp, which is everything needed to answer "is this key alive
 * and is it the one I think it is" without anyone being able to walk away with it.
 */
export function IntegrationsPage() {
  const canRead = useCan('integration:read')
  const canKeys = useCan('apikey:read')

  const [params, setParams] = useSearchParams()
  const raw = params.get('view')
  const view = raw && VIEWS.some((v) => v.id === raw) ? raw : 'registry'
  const setView = (id: string) => {
    const sp = new URLSearchParams(params)
    sp.set('view', id)
    setParams(sp, { replace: true })
  }

  const navigate = useNavigate()

  const integrations = useLoad(() => (canRead ? listIntegrations() : Promise.resolve([])), [canRead])

  if (!canRead && !canKeys) return <Forbidden what="integrations" />

  const rows = integrations.data ?? []
  const healthy = rows.filter((r) => r.health === 'HEALTHY').length
  const failing = rows.filter((r) => r.health === 'DOWN' || r.status === 'ERROR').length
  const limited = rows.filter((r) => r.status === 'RATE_LIMITED').length

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Integrations"
        description="Third-party providers, credentials, inbound callbacks and the keys we issue."
        right={
          <button type="button" className="erp-btn" onClick={() => void integrations.reload()} aria-label="Refresh">
            <RefreshCw className="h-3.5 w-3.5" /> <span className="axis-hide-sm">Refresh</span>
          </button>
        }
      />

      <div className="border-b border-border">
        <div className="flex gap-1 overflow-x-auto">
          {VIEWS.map((v) => (
            <button
              key={v.id}
              type="button"
              onClick={() => setView(v.id)}
              className={cn(
                'px-3 py-2 text-erp-sm border-b-2 -mb-px inline-flex items-center gap-1.5 whitespace-nowrap',
                view === v.id
                  ? 'border-chocolate text-chocolate font-medium'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              <v.icon className="h-3.5 w-3.5" />
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {view === 'registry' && (
        <>
          <KpiStrip>
            <MetricCard label="Configured" value={fmtNumber(rows.length)} accent />
            <MetricCard label="Healthy" value={fmtNumber(healthy)} />
            <MetricCard label="Failing" value={fmtNumber(failing)} deltaType={failing ? 'down' : 'neutral'} />
            <MetricCard label="Rate limited" value={fmtNumber(limited)} />
          </KpiStrip>

          <Panel bodyClassName="p-0">
            <ErrorStrip message={integrations.error} />
            <ErpTable head={<>
              <Th>Integration</Th>
              <Th className="axis-hide-md">Provider</Th>
              <Th className="axis-hide-lg">Auth</Th>
              <Th>Env</Th>
              <Th>Status</Th>
              <Th>Health</Th>
              <Th className="axis-hide-lg">Today</Th>
            </>}>
              {integrations.loading && <LoadingRows colSpan={7} />}
              {!integrations.loading && rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-erp-sm text-muted-foreground">
                    No integrations configured. The system starts empty — nothing is seeded.
                  </td>
                </tr>
              )}
              {!integrations.loading && rows.map((r: IntegrationResponse) => (
                <tr
                  key={r.id}
                  className="hover:bg-muted/40 cursor-pointer"
                  onClick={() => navigate(`/integrations/${r.id}`)}
                  tabIndex={0}
                  role="button"
                  aria-label={`Open ${r.displayName}`}
                  onKeyDown={(e) => {
                    // Only when the row itself has focus, so Enter on a nested
                    // control does not also fire the row.
                    if (e.target !== e.currentTarget) return
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      navigate(`/integrations/${r.id}`)
                    }
                  }}
                >
                  <Td className="font-medium">
                    {r.displayName}
                    <div className="text-erp-xs text-muted-foreground font-mono">{r.code}</div>
                  </Td>
                  <Td className="axis-hide-md">{r.provider}</Td>
                  <Td className="axis-hide-lg text-erp-xs">{AUTH_KIND_LABEL[r.authKind] ?? r.authKind}</Td>
                  <Td>
                    <span className={cn(
                      'text-erp-xs px-1.5 py-0.5 rounded-md border',
                      r.environment === 'PRODUCTION'
                        ? 'border-chocolate/40 text-chocolate bg-chocolate-soft/40'
                        : 'border-border text-muted-foreground',
                    )}>
                      {r.environment === 'PRODUCTION' ? 'prod' : 'sandbox'}
                    </span>
                  </Td>
                  <Td><StatusBadge status={r.status.toLowerCase()} /></Td>
                  <Td>
                    <span className={cn('inline-flex items-center gap-1 text-erp-xs', HEALTH_STYLE[r.health])}>
                      <Activity className="h-3 w-3" />
                      {humanise(r.health)}
                    </span>
                  </Td>
                  <Td className="axis-hide-lg erp-metric text-erp-sm tabular-nums">
                    {fmtNumber(r.requestsToday)}
                  </Td>
                </tr>
              ))}
            </ErpTable>
            <div className="px-4 py-2 border-t border-border bg-muted/20 text-erp-xs text-muted-foreground">
              Credentials are write-only. No screen in this application can display a
              stored secret, because the API has no endpoint that returns one.
            </div>
          </Panel>
        </>
      )}

      {view === 'inbound' && <InboundEventsTab />}
      {view === 'webhooks' && <WebhooksTab />}
      {view === 'keys' && (canKeys ? <ApiKeysTab /> : <Forbidden what="API keys" />)}

    </div>
  )
}

/**
 * The inbound callback log.
 *
 * `signatureStatus` is the column that matters. A row landing `INVALID` is
 * stored and NOT dispatched — the backend refuses to hand it to a handler — and
 * a run of them is how you notice someone probing the endpoint. That is why they
 * are shown rather than filtered out of the default view.
 */
function InboundEventsTab() {
  const events = useLoad(() => listInboundEvents(), [])
  const rows = events.data ?? []
  const invalid = rows.filter((e) => e.signatureStatus === 'INVALID').length

  return (
    <Panel bodyClassName="p-0">
      <ErrorStrip message={events.error} />
      {invalid > 0 && (
        <div className="px-4 py-2 text-erp-sm text-amber-800 bg-amber-50/60 border-b border-amber-200">
          {invalid} event{invalid === 1 ? '' : 's'} arrived with an invalid signature. They were
          stored and not dispatched.
        </div>
      )}
      <ErpTable head={<>
        <Th>Received</Th>
        <Th>Event</Th>
        <Th>Signature</Th>
        <Th>Status</Th>
        <Th className="axis-hide-md">Attempts</Th>
        <Th className="axis-hide-lg">Error</Th>
      </>}>
        {events.loading && <LoadingRows colSpan={6} />}
        {!events.loading && rows.length === 0 && (
          <tr>
            <td colSpan={6} className="px-4 py-10 text-center text-erp-sm text-muted-foreground">
              No callbacks received yet.
            </td>
          </tr>
        )}
        {!events.loading && rows.map((e) => (
          <tr key={e.id} className="hover:bg-muted/40">
            <Td className="whitespace-nowrap">{fmtDateTime(e.receivedAt)}</Td>
            <Td className="font-mono text-erp-xs">{e.eventType ?? '—'}</Td>
            <Td>
              <span className={cn(
                'text-erp-xs',
                e.signatureStatus === 'VALID'
                  ? 'text-emerald-700 dark:text-emerald-400'
                  : 'text-red-700 dark:text-red-400',
              )}>
                {humanise(e.signatureStatus)}
              </span>
            </Td>
            <Td><StatusBadge status={e.status.toLowerCase()} /></Td>
            <Td className="axis-hide-md erp-metric">{e.attempts}</Td>
            <Td className="axis-hide-lg text-erp-xs text-muted-foreground truncate max-w-[16rem]">
              {e.errorDetail ?? '—'}
            </Td>
          </tr>
        ))}
      </ErpTable>
    </Panel>
  )
}


export default IntegrationsPage
